import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SafetyQuiz = ({ module, user }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const questions = module.quiz_questions;
  const allAnswered = questions.every((_, idx) => answers[idx] !== undefined);

  const handleSelect = (questionIdx, optionIdx) => {
    if (result) return;
    setAnswers((prev) => ({ ...prev, [questionIdx]: optionIdx }));
  };

  const handleSubmit = async () => {
    if (!allAnswered) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    const answerList = questions.map((_, idx) => answers[idx]);

    if (user) {
      setSubmitting(true);
      try {
        const { data } = await axios.post(
          `${API}/safety-modules/${module.id}/submit-quiz`,
          { answers: answerList },
          { withCredentials: true }
        );
        setResult(data);
        if (data.passed) {
          toast.success(`Congratulations! You passed with ${data.score}%`);
        } else {
          toast.error(`You scored ${data.score}%. Passing score is ${module.passing_score}%. Try again!`);
        }
      } catch (error) {
        toast.error('Failed to submit quiz');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Grade locally for guests
      const correctCount = questions.filter((q, idx) => answers[idx] === q.correct_answer).length;
      const score = Math.round((correctCount / questions.length) * 100);
      setResult({
        score,
        passed: score >= module.passing_score,
        correct_answers: correctCount,
        total_questions: questions.length,
        guest: true,
      });
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="bg-white border border-borders rounded-sm p-8" data-testid="safety-quiz">
      <h2 className="text-lg font-semibold text-primary mb-2">Knowledge Check Quiz</h2>
      <p className="text-sm text-gray-600 mb-2">
        Answer all {questions.length} questions. Passing score: {module.passing_score}%.
      </p>
      {!user && !result && (
        <p className="text-xs text-accent mb-6" data-testid="quiz-guest-note">
          You can take this quiz as a guest, but log in to save your score and completion record.
        </p>
      )}
      {(user || result) && <div className="mb-6" />}

      {result && (
        <div
          className={`mb-8 p-6 rounded-sm border ${result.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
          data-testid="quiz-result"
        >
          <div className="flex items-center gap-3 mb-2">
            {result.passed ? (
              <CheckCircle size={32} className="text-green-600" weight="fill" />
            ) : (
              <XCircle size={32} className="text-red-600" weight="fill" />
            )}
            <span className="text-2xl font-bold text-primary" data-testid="quiz-score">{result.score}%</span>
            <span className={`text-sm font-semibold ${result.passed ? 'text-green-700' : 'text-red-700'}`}>
              {result.passed ? 'PASSED' : 'NOT PASSED'}
            </span>
          </div>
          <p className="text-sm text-gray-700">
            You answered {result.correct_answers} of {result.total_questions} questions correctly.
            {result.guest && ' Log in to save your progress and completion record.'}
          </p>
          <button
            onClick={handleRetake}
            className="mt-4 flex items-center gap-2 text-sm text-accent font-semibold hover:underline"
            data-testid="quiz-retake-btn"
          >
            <ArrowCounterClockwise size={16} /> Retake Quiz
          </button>
        </div>
      )}

      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <div key={q.question} data-testid={`quiz-question-${qIdx}`}>
            <p className="font-medium text-primary mb-3">
              {qIdx + 1}. {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((option, oIdx) => {
                const isSelected = answers[qIdx] === oIdx;
                const showCorrect = result && oIdx === q.correct_answer;
                const showWrong = result && isSelected && oIdx !== q.correct_answer;
                return (
                  <button
                    key={option}
                    onClick={() => handleSelect(qIdx, oIdx)}
                    disabled={!!result}
                    data-testid={`quiz-q${qIdx}-option-${oIdx}`}
                    className={`w-full text-left px-4 py-3 rounded-sm border text-sm transition-colors ${
                      showCorrect
                        ? 'bg-green-50 border-green-400 text-green-900'
                        : showWrong
                        ? 'bg-red-50 border-red-400 text-red-900'
                        : isSelected
                        ? 'bg-accent/10 border-accent text-primary'
                        : 'bg-white border-borders hover:border-accent text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            {result && q.explanation && (
              <p className="mt-2 text-xs text-gray-600 bg-gray-50 p-3 rounded-sm border border-borders">
                <span className="font-semibold">Explanation:</span> {q.explanation}
              </p>
            )}
          </div>
        ))}
      </div>

      {!result && (
        <button
          onClick={handleSubmit}
          disabled={submitting || !allAnswered}
          className="mt-8 bg-accent text-white px-8 py-3 rounded-sm hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          data-testid="quiz-submit-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Quiz'}
        </button>
      )}
    </div>
  );
};
